import React, { useState, useEffect } from 'react';
import GeoLocationService from '../../../services/geoLocationService';
import styles from './GeoLocationTester.module.css';

const GeoLocationTester = () => {
  const [currentLocation, setCurrentLocation] = useState(null);
  const [testScenarios, setTestScenarios] = useState({});
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Only show in testing mode AND for specific test user
    if (GeoLocationService.TESTING_MODE && isTestUser()) {
      setTestScenarios(GeoLocationService.getTestScenarios());
      loadCurrentLocation();
    }
  }, []);

  const isTestUser = () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user.email === 'test@trakvu.com';
    } catch (error) {
      return false;
    }
  };

  const loadCurrentLocation = () => {
    // Prioritize override location if it exists, otherwise use real location
    const overrideLocation = JSON.parse(localStorage.getItem('geoLocationOverride') || 'null');
    const realLocation = JSON.parse(localStorage.getItem('userLocation') || '{}');
    
    const activeLocation = overrideLocation || realLocation;
    setCurrentLocation(activeLocation);
  };

  const getCurrentScenarioKey = () => {
    if (!currentLocation) return null;
    
    // Check which test scenario matches the current location
    for (const [key, scenario] of Object.entries(testScenarios)) {
      const { countryCode, region } = scenario.config;
      
      if (currentLocation.countryCode === countryCode) {
        // For US, also check region for California
        if (countryCode === 'US') {
          if (region === 'California' && currentLocation.region?.includes('California')) {
            return key;
          } else if (region === 'Texas' && !currentLocation.region?.includes('California')) {
            return key; // Normal US
          }
        }
        // For Canada, check region for Quebec
        else if (countryCode === 'CA') {
          if (region === 'Quebec' && currentLocation.region?.includes('Quebec')) {
            return key;
          }
        }
        // For other countries, just match country code
        else {
          return key;
        }
      }
    }
    
    return null;
  };

  const handleScenarioSelect = async (scenarioKey) => {
    const scenario = testScenarios[scenarioKey];
    if (scenario) {
      const { countryCode, region, city } = scenario.config;
      GeoLocationService.setLocationOverride(countryCode, region, city);
      
      // Reload the page to apply changes
      window.location.reload();
    }
  };

  const handleClearOverride = () => {
    GeoLocationService.clearLocationOverride();
    window.location.reload();
  };

  const getLocationDisplay = () => {
    if (!currentLocation) return 'Unknown';
    
    const parts = [currentLocation.city, currentLocation.region, currentLocation.country];
    return parts.filter(Boolean).join(', ');
  };

  const getComplianceFlags = () => {
    if (!currentLocation) return [];
    
    const flags = [];
    if (currentLocation.isChina) flags.push('🚫 China Blocked');
    if (currentLocation.isEU) flags.push('🇪🇺 EU/GDPR');
    if (currentLocation.countryCode === 'GB') flags.push('🇬🇧 UK GDPR');
    if (currentLocation.countryCode === 'US' && currentLocation.region?.includes('California')) {
      flags.push('🇺🇸 CPRA (California)');
    }
    if (currentLocation.countryCode === 'CA' && currentLocation.region?.includes('Quebec')) {
      flags.push('🇨🇦 Quebec Law 25');
    }
    if (currentLocation.requiresCookieConsent) flags.push('🍪 Cookie Consent');
    if (currentLocation.requiresDataProtection) flags.push('🔒 Data Protection');
    if (currentLocation.isTestOverride) flags.push('🧪 Test Override');
    
    return flags;
  };

  if (!GeoLocationService.TESTING_MODE || !isTestUser()) {
    return null; // Don't show in production or for non-test users
  }

  return (
    <>
      {/* Floating Toggle Button */}
      <button 
        className={styles.toggleButton}
        onClick={() => setIsVisible(!isVisible)}
        title="Geolocation Tester"
      >
        🌍
      </button>

      {/* Testing Panel */}
      {isVisible && (
        <div className={styles.testerPanel}>
          <div className={styles.header}>
            <h3>🧪 Geolocation Tester</h3>
            <button 
              className={styles.closeButton}
              onClick={() => setIsVisible(false)}
            >
              ×
            </button>
          </div>

          <div className={styles.currentLocation}>
            <h4>Current Location</h4>
            <p className={styles.locationText}>{getLocationDisplay()}</p>
            
            <div className={styles.flags}>
              {getComplianceFlags().map((flag, index) => (
                <span key={index} className={styles.flag}>
                  {flag}
                </span>
              ))}
            </div>
          </div>

          <div className={styles.scenarios}>
            <h4>Test Scenarios</h4>
            <div className={styles.scenarioGrid}>
              {Object.entries(testScenarios).map(([key, scenario]) => {
                const isActive = getCurrentScenarioKey() === key;
                return (
                  <button
                    key={key}
                    className={`${styles.scenarioButton} ${isActive ? styles.activeScenario : ''}`}
                    onClick={() => handleScenarioSelect(key)}
                  >
                    <span className={styles.scenarioName}>{scenario.name}</span>
                    {isActive && (
                      <span className={styles.activeIndicator}>
                        <i className="fas fa-check-circle"></i>
                        Active
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className={styles.actions}>
            <button 
              className={styles.clearButton}
              onClick={handleClearOverride}
            >
              🔄 Reset to Real Location
            </button>
          </div>

          <div className={styles.instructions}>
            <p><strong>Instructions:</strong></p>
            <ul>
              <li>Click any scenario to test regional behavior</li>
              <li>Page will reload to apply changes</li>
              <li>Check console for detailed location info</li>
              <li>Reset to get your real location back</li>
            </ul>
          </div>
        </div>
      )}
    </>
  );
};

export default GeoLocationTester; 